import React, { useState, useMemo } from 'react';
import { AlertTriangle, Mail, X } from 'lucide-react';
import {
  AccommodationBooking,
  EarlyCheckoutRoomParams,
} from '../../../../types/accommodation';
import { apiError } from '../../../../util/notify';
import { useEarlyCheckout } from '../../../../hooks/accommodation';
import { DatePicker } from '../../../common/DatePicker';

interface EarlyCheckoutModalProps {
  isOpen: boolean;
  booking: AccommodationBooking | null;
  onClose: () => void;
}

const addDays = (isoDate: string, days: number): string => {
  const d = new Date(isoDate);
  d.setDate(d.getDate() + days);
  return d.toISOString().split('T')[0];
};

const getDaysBetween = (start: string, end: string): number => {
  const s = new Date(start);
  const e = new Date(end);
  s.setHours(12, 0, 0, 0);
  e.setHours(12, 0, 0, 0);
  const diffMs = e.getTime() - s.getTime();
  return Math.max(0, Math.round(diffMs / 86_400_000));
};

export const EarlyCheckoutModal: React.FC<EarlyCheckoutModalProps> = ({
  isOpen,
  booking,
  onClose,
}) => {
  const earlyCheckoutMutation = useEarlyCheckout();
  const isSubmitting = earlyCheckoutMutation.isPending;

  // Track the new checkout date for each room booking row (default to booking's check_out)
  const [roomDates, setRoomDates] = useState<Record<string, string>>({});

  // Initialize roomDates when modal is opened and booking is available
  React.useEffect(() => {
    if (isOpen && booking && booking.booking_rooms) {
      const dates: Record<string, string> = {};
      booking.booking_rooms.forEach((br) => {
        dates[br.id] = booking.check_out;
      });
      setRoomDates(dates);
    }
  }, [isOpen, booking]);

  const [notifyGuest, setNotifyGuest] = useState(true);

  // Recalculate per-room nights, subtotals, and overall booking totals
  const recalculatedData = useMemo(() => {
    if (!booking || !booking.booking_rooms) {
      return { rooms: [], newTotal: 0, hasChanged: false, newCheckOut: '' };
    }

    let newTotal = 0;
    let hasChanged = false;
    let latestDate = booking.check_in;

    const rooms: Array<
      EarlyCheckoutRoomParams & {
        roomNumber: string;
        roomTypeName: string;
        originalNights: number;
        originalSubtotal: number;
        pricePerNight: number;
      }
    > = booking.booking_rooms.map((br) => {
      const selectedDate = roomDates[br.id] || booking.check_out;
      const nights = getDaysBetween(booking.check_in, selectedDate);
      const subtotal = nights * br.price_per_night;

      if (selectedDate !== booking.check_out) {
        hasChanged = true;
      }

      if (new Date(selectedDate) > new Date(latestDate)) {
        latestDate = selectedDate;
      }

      newTotal += subtotal;

      return {
        bookingRoomId: br.id,
        roomId: br.room_id,
        newCheckOut: selectedDate,
        nights,
        subtotal,
        roomNumber: br.room?.room_number ?? '—',
        roomTypeName: br.room?.room_type?.name ?? '',
        originalNights: br.nights,
        originalSubtotal: br.subtotal,
        pricePerNight: br.price_per_night,
      };
    });

    return {
      rooms,
      newTotal,
      hasChanged,
      newCheckOut: latestDate,
    };
  }, [booking, roomDates]);

  if (!isOpen || !booking) return null;

  const handleDateChange = (bookingRoomId: string, value: string) => {
    setRoomDates((prev) => ({
      ...prev,
      [bookingRoomId]: value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!recalculatedData.hasChanged) return;

    // Check if newCheckOut exceeds check_in and satisfies database constraint
    if (new Date(recalculatedData.newCheckOut) <= new Date(booking.check_in)) {
      apiError('Checkout date must be after check-in date.');
      return;
    }

    const todayStr = new Date().toISOString().split('T')[0];
    const allRoomsCheckedOut = recalculatedData.rooms.every(
      (r) => r.newCheckOut <= todayStr,
    );

    const payload = {
      bookingId: booking.id,
      checkIn: booking.check_in,
      originalCheckOut: booking.check_out,
      rooms: recalculatedData.rooms.filter(
        (r) => r.newCheckOut !== booking.check_out,
      ),
      newCheckOut: recalculatedData.newCheckOut,
      newTotalAmount: recalculatedData.newTotal,
      allRoomsCheckedOut,
      notifyGuest,
    };

    earlyCheckoutMutation.mutate(payload, {
      onSuccess: () => {
        onClose();
      },
    });
  };

  const guestSaves = booking.total_amount - recalculatedData.newTotal;
  const { currency } = booking;

  return (
    // eslint-disable-next-line jsx-a11y/no-static-element-interactions
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4 overflow-y-auto"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="w-full max-w-lg rounded-2xl bg-white shadow-2xl flex flex-col my-8">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-gray-100 px-5 py-4 shrink-0">
          <div>
            <h3 className="text-sm font-semibold text-gray-900">
              Early Checkout
            </h3>
            <p className="text-xs text-gray-400">
              {booking.client
                ? `${booking.client.first_name} ${booking.client.last_name}`
                : `Booking #${booking.id.slice(0, 8).toUpperCase()}`}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg p-1.5 text-gray-400 hover:bg-gray-100"
          >
            <X size={15} />
          </button>
        </div>

        {/* Form */}
        <form
          onSubmit={handleSubmit}
          className="flex-1 overflow-y-auto p-5 space-y-4 max-h-[75vh]"
        >
          {booking.ota_collects_payment && (
            <div className="flex gap-2.5 rounded-xl border border-amber-200 bg-amber-50 p-3.5">
              <AlertTriangle
                size={16}
                className="text-amber-600 shrink-0 mt-0.5"
              />
              <div>
                <p className="text-xs font-semibold text-amber-800">
                  OTA Collects Payment
                </p>
                <p className="text-xs text-amber-700 mt-0.5 leading-relaxed">
                  This booking is collected by an OTA (
                  {booking.source_name || booking.source}). Please make sure to
                  update the OTA channel manager separately to avoid payment
                  mismatches.
                </p>
              </div>
            </div>
          )}

          {/* Per Room Checkout Adjustment */}
          <div className="space-y-4">
            {recalculatedData.rooms.map((room) => {
              const minDate = addDays(booking.check_in, 1);
              const maxDate = booking.check_out;
              const hasRoomChanged = room.newCheckOut !== booking.check_out;

              return (
                <div
                  key={room.bookingRoomId}
                  className="rounded-xl border border-gray-100 bg-slate-50/50 p-4 space-y-3"
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-semibold text-gray-900 font-sans">
                        Room {room.roomNumber}
                      </p>
                      <p className="text-xs text-gray-400 font-medium mt-0.5">
                        {room.roomTypeName}
                      </p>
                    </div>
                    {hasRoomChanged && (
                      <span className="rounded-full bg-emerald-50 px-2.5 py-0.5 text-[10px] font-semibold text-emerald-700">
                        Early Checkout Selected
                      </span>
                    )}
                  </div>

                  <div className="grid gap-3 sm:grid-cols-2">
                    <div>
                      <span className="block text-[10px] text-gray-400 font-medium mb-1">
                        Original Checkout
                      </span>
                      <span className="text-xs font-semibold text-gray-700 block bg-gray-50 border border-gray-200 rounded-xl px-3 py-2">
                        {new Date(booking.check_out).toLocaleDateString(
                          undefined,
                          {
                            day: 'numeric',
                            month: 'short',
                            year: 'numeric',
                          },
                        )}
                      </span>
                    </div>

                    <div>
                      <span className="block text-[10px] text-gray-400 font-medium mb-1">
                        New Checkout Date
                      </span>
                      <DatePicker
                        mode="single"
                        minDate={minDate}
                        maxDate={maxDate}
                        value={room.newCheckOut}
                        onChange={(iso) =>
                          handleDateChange(room.bookingRoomId, iso)
                        }
                        placeholder="Select checkout"
                        popoverAlign="right"
                        className="py-1.5 text-xs font-medium"
                      />
                    </div>
                  </div>

                  <div className="flex items-center justify-between pt-2 text-[10px] border-t border-dashed border-gray-200">
                    <div className="text-gray-400 font-medium">
                      Original: {room.originalNights} nights = {currency}{' '}
                      {room.originalSubtotal.toLocaleString()}
                    </div>
                    <div className="font-semibold text-gray-800">
                      New: {room.nights} nights = {currency}{' '}
                      {room.subtotal.toLocaleString()}
                      {hasRoomChanged && guestSaves > 0 && (
                        <span className="text-emerald-600 ml-1.5 font-bold">
                          (saves {currency}{' '}
                          {(
                            room.originalSubtotal - room.subtotal
                          ).toLocaleString()}
                          )
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Summary Section */}
          {recalculatedData.hasChanged && (
            <div className="rounded-xl bg-slate-50 border border-gray-100 p-4 space-y-2.5">
              <div className="flex justify-between text-xs text-gray-500 font-medium">
                <span>Old booking total:</span>
                <span>
                  {currency} {booking.total_amount.toLocaleString()}
                </span>
              </div>
              <div className="flex justify-between text-xs font-bold text-gray-800">
                <span>New booking total:</span>
                <span>
                  {currency} {recalculatedData.newTotal.toLocaleString()}
                </span>
              </div>
              {guestSaves > 0 ? (
                <div className="flex justify-between text-xs font-bold text-emerald-600 pt-1.5 border-t border-dashed border-gray-200">
                  <span>Guest saves:</span>
                  <span>
                    {currency} {guestSaves.toLocaleString()}
                  </span>
                </div>
              ) : guestSaves < 0 ? (
                <div className="flex justify-between text-xs font-bold text-red-600 pt-1.5 border-t border-dashed border-gray-200">
                  <span>Additional owed:</span>
                  <span>
                    {currency} {Math.abs(guestSaves).toLocaleString()}
                  </span>
                </div>
              ) : null}
            </div>
          )}

          {/* Notify guest toggle */}
          <label
            htmlFor="notify-guest-toggle"
            className="flex items-center justify-between py-1 px-1 cursor-pointer select-none"
          >
            <div className="flex items-center gap-2">
              <Mail size={15} className="text-gray-400" />
              <span className="text-xs text-gray-600 font-medium">
                Notify guest & team via email
              </span>
            </div>
            <div className="relative inline-flex items-center">
              <input
                id="notify-guest-toggle"
                type="checkbox"
                checked={notifyGuest}
                onChange={(e) => setNotifyGuest(e.target.checked)}
                className="sr-only peer"
              />
              <div className="w-9 h-5 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-pos-primary" />
            </div>
          </label>

          {/* Action Buttons */}
          <div className="flex justify-end gap-2.5 pt-2 border-t border-gray-100 shrink-0">
            <button
              type="button"
              onClick={onClose}
              className="rounded-xl border border-gray-300 px-4 py-2 text-xs font-medium text-gray-700 hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={!recalculatedData.hasChanged || isSubmitting}
              className="rounded-xl bg-pos-primary px-5 py-2 text-xs font-medium text-white hover:bg-pos-primary/95 disabled:opacity-50 flex items-center gap-1.5"
            >
              {isSubmitting ? 'Confirming...' : 'Confirm Early Checkout'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
